import type { SessionUserSnapshot } from "@/lib/auth-server-fns";
import type { ViewerContext } from "@/lib/viewer-context";
import { DEFAULT_VIEWER_LOCALE, type ViewerLocale } from "@garimpa/lib/viewerPreferences";

/**
 * Shape the root route's `beforeLoad` contributes to every descendant match's
 * `context`. Child routes' `head({ match })` handlers read the active locale
 * + message tree from `match.context` (TanStack merges ancestor beforeLoad
 * returns into each match's context via `buildMatchContext`).
 *
 * We deliberately use `context` over `loaderData` here because `head()`
 * receives a pre-load snapshot of the `matches` array whose `loaderData`
 * slots are never populated — only `match.context` is live at head time.
 */
export interface RootLoaderData {
  viewer?: ViewerContext;
  user?: SessionUserSnapshot | null;
  locale: ViewerLocale;
  /**
   * `any` (not `unknown`) because `use-intl`'s `createTranslator<TMessages>`
   * infers the translate function's key type from `TMessages`. With
   * `Record<string, unknown>`, `NestedKeyOf<TMessages>` collapses to `never`
   * and every `t("...")` call fails. `Record<string, any>` mirrors the
   * library's own default `IntlMessages` alias and keeps key access
   * permissive at the cost of autocompletion.
   */
  messages: Record<string, any>;
  /**
   * IANA time-zone passed to `<IntlProvider timeZone=...>` so date/time
   * formatting is stable across server render + client hydration. Server
   * defaults to `"UTC"` (neutral); a client-only effect upgrades the provider
   * to the browser's actual zone after mount.
   */
  timeZone: string;
  /**
   * Server-issued "now" so `useFormatter().relativeTime(...)` renders the
   * same text on the server and during client hydration. Serialised as an
   * ISO string through the context; revived into a `Date` before being
   * handed to `<IntlProvider>`.
   */
  now: string;
}

// Intentionally loose: TanStack Router infers strongly-typed context
// intersections that lack a `[string]: unknown` index signature, so we accept
// any object-ish value and cast inside the function.
type MatchWithContext = { context?: unknown } | undefined;

/**
 * Reads the root-contributed i18n fields from a TanStack route match's
 * `context`. Pass `match` from a `head({ match })` handler.
 *
 * Falls back to safe defaults if the context hasn't populated yet (transient
 * dev-server states, first render before `beforeLoad` resolves) so `head()`
 * never crashes — the real metadata reconciles on the next head evaluation.
 *
 * Usage:
 * ```ts
 * head: ({ match }) => {
 *   const { locale, messages } = getRootLoaderData(match);
 *   // ...
 * }
 * ```
 */
export function getRootLoaderData(match: MatchWithContext): RootLoaderData {
  const ctx = match?.context as Partial<RootLoaderData> | undefined;
  if (ctx?.locale && ctx.messages) {
    return {
      viewer: ctx.viewer,
      user: ctx.user,
      locale: ctx.locale,
      messages: ctx.messages,
      timeZone: ctx.timeZone ?? "UTC",
      now: ctx.now ?? new Date(0).toISOString(),
    };
  }
  return {
    locale: DEFAULT_VIEWER_LOCALE,
    messages: {},
    timeZone: "UTC",
    now: new Date(0).toISOString(),
  };
}
